/**
 * Admin dashboard with tabbed navigation
 * Tabs are conditionally rendered based on user permissions
 * Uses Lucide icons for unified UX pattern
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../services/api';
import { usePermission } from '../hooks/usePermission';
import AdminOverview from '../components/admin/AdminOverview';
import ProductManagement from '../components/admin/ProductManagement';
import OrderManagement from '../components/admin/OrderManagement';
import UserManagement from '../components/admin/UserManagement';
import RoleManagement from '../components/admin/RoleManagement';
import Analytics from '../components/admin/Analytics';
import Settings from '../components/admin/Settings';
import HelpManagement from '../components/admin/HelpManagement';
import ContactManagement from '../components/admin/ContactManagement';
import {
  LayoutDashboard,
  BarChart3,
  Package,
  ShoppingCart,
  Users,
  Shield,
  HelpCircle,
  MessageSquare,
  Settings as SettingsIcon,
  LucideIcon,
} from 'lucide-react';

type AdminTab = 'overview' | 'products' | 'orders' | 'users' | 'roles' | 'analytics' | 'help' | 'contact' | 'settings';

interface TabConfig {
  id: AdminTab;
  label: string;
  icon: LucideIcon;
  hasPermission: boolean;
}

export default function AdminPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState({
    // Basic counts
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    // Product breakdown
    publishedProducts: 0,
    draftProducts: 0,
    // Order breakdown
    completedOrders: 0,
    pendingOrders: 0,
    refundedOrders: 0,
    // Revenue
    totalRevenue: 0,
    // Users
    adminUsers: 0,
    // Contacts
    newContacts: 0,
    // Rates
    conversionRate: 0,
    refundRate: 0,
  });
  const [loading, setLoading] = useState(true);

  // Permission checking
  const { canAny } = usePermission();

  // Memoize visible tabs to prevent recalculation on every render
  const visibleTabs = useMemo(() => {
    const allTabs: TabConfig[] = [
      {
        id: 'overview',
        label: 'Overview',
        icon: LayoutDashboard,
        hasPermission: true,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: BarChart3,
        hasPermission: canAny(['analytics:admin', 'analytics:*']),
      },
      {
        id: 'products',
        label: 'Products',
        icon: Package,
        hasPermission: canAny(['product:admin', 'product:*']),
      },
      {
        id: 'orders',
        label: 'Orders',
        icon: ShoppingCart,
        hasPermission: canAny(['order:admin', 'order:*']),
      },
      {
        id: 'users',
        label: 'Users',
        icon: Users,
        hasPermission: canAny(['user:admin', 'user:*']),
      },
      {
        id: 'roles',
        label: 'Roles',
        icon: Shield,
        hasPermission: canAny(['user:admin', 'user:*']),
      },
      {
        id: 'help',
        label: 'Help Center',
        icon: HelpCircle,
        hasPermission: canAny(['help:admin', 'help:*']),
      },
      {
        id: 'contact',
        label: 'Contact',
        icon: MessageSquare,
        hasPermission: canAny(['contact:read', 'contact:*', 'settings:admin']),
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: SettingsIcon,
        hasPermission: canAny(['settings:admin', 'settings:*']),
      },
    ];
    return allTabs.filter((tab) => tab.hasPermission);
  }, [canAny]);

  // Active tab state with auto-selection of first available tab
  const [activeTab, setActiveTab] = useState<AdminTab>(() => {
    const requestedTab = searchParams.get('tab') as AdminTab;
    const isTabVisible = visibleTabs.some((tab) => tab.id === requestedTab);
    return isTabVisible ? requestedTab : visibleTabs[0]?.id || 'overview';
  });

  useEffect(() => {
    loadStats();
  }, []);

  // Update active tab when URL changes, but only if tab is visible
  useEffect(() => {
    const requestedTab = searchParams.get('tab') as AdminTab;
    if (requestedTab) {
      const isTabVisible = visibleTabs.some((tab) => tab.id === requestedTab);
      if (isTabVisible) {
        setActiveTab(requestedTab);
      } else {
        // If requested tab not visible, redirect to first available tab
        setActiveTab(visibleTabs[0]?.id || 'overview');
        setSearchParams({ tab: visibleTabs[0]?.id || 'overview' });
      }
    }
  }, [searchParams, visibleTabs, setSearchParams]);

  const loadStats = async () => {
    try {
      const data = await adminApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  return (
    <div className="container-custom py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage products, orders, and users
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="flex space-x-1 overflow-x-auto" aria-label="Admin tabs">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  inline-flex items-center gap-2 py-4 px-4 border-b-2 font-medium text-sm whitespace-nowrap
                  transition-colors duration-200
                  ${
                    isActive
                      ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content - simplified, permission check already done via visibleTabs */}
      <div className="animate-fade-in">
        {activeTab === 'overview' && <AdminOverview stats={stats} loading={loading} onRefresh={loadStats} />}
        {activeTab === 'analytics' && <Analytics />}
        {activeTab === 'products' && <ProductManagement />}
        {activeTab === 'orders' && <OrderManagement />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'roles' && <RoleManagement />}
        {activeTab === 'help' && <HelpManagement />}
        {activeTab === 'contact' && <ContactManagement />}
        {activeTab === 'settings' && <Settings />}
      </div>
    </div>
  );
}
